import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import {
  FiservCardType,
  FiservConfig,
  FiservInstallment,
  FiservInstallmentInput,
  OfflinePaymentMethod,
  GetnetConfig,
  InstructionsPaymentConfig,
  MercadoPagoConfig,
  PaymentMethodCode,
} from "./types";

const base = (sellerCode: string) =>
  `${API_BASE_URL}/sellers/${encodeURIComponent(sellerCode)}/payments/methods`;

// Instructions-only methods (purchase-order, bank-transfer, check-money)
export async function getInstructionsMethod(
  sellerCode: string,
  code: 'purchase-order' | 'bank-transfer' | 'check-money',
): Promise<InstructionsPaymentConfig> {
  const { data } = await axiosInstance.get<InstructionsPaymentConfig>(`${base(sellerCode)}/${code}`);
  return data;
}

export async function upsertInstructionsMethod(
  sellerCode: string,
  code: 'purchase-order' | 'bank-transfer' | 'check-money',
  payload: InstructionsPaymentConfig,
): Promise<InstructionsPaymentConfig> {
  const { data } = await axiosInstance.put<InstructionsPaymentConfig>(
    `${base(sellerCode)}/${code}`,
    payload,
  );
  return data;
}

// Getnet
export async function getGetnet(sellerCode: string): Promise<GetnetConfig> {
  const { data } = await axiosInstance.get<GetnetConfig>(`${base(sellerCode)}/getnet`);
  return data;
}

export async function upsertGetnet(sellerCode: string, payload: GetnetConfig): Promise<GetnetConfig> {
  const { data } = await axiosInstance.put<GetnetConfig>(`${base(sellerCode)}/getnet`, payload);
  return data;
}

// MercadoPago
export async function getMercadoPago(sellerCode: string): Promise<MercadoPagoConfig> {
  const { data } = await axiosInstance.get<MercadoPagoConfig>(`${base(sellerCode)}/mercadopago`);
  return data;
}

export async function upsertMercadoPago(
  sellerCode: string,
  payload: MercadoPagoConfig,
): Promise<MercadoPagoConfig> {
  const { data } = await axiosInstance.put<MercadoPagoConfig>(
    `${base(sellerCode)}/mercadopago`,
    payload,
  );
  return data;
}

// Fiserv
export async function getFiserv(sellerCode: string): Promise<FiservConfig> {
  const { data } = await axiosInstance.get<FiservConfig>(`${base(sellerCode)}/fiserv`);
  return data;
}

export async function upsertFiserv(sellerCode: string, payload: FiservConfig): Promise<FiservConfig> {
  const { data } = await axiosInstance.put<FiservConfig>(`${base(sellerCode)}/fiserv`, payload);
  return data;
}

// List configured (for badges/status)
export async function listPaymentMethods(
  sellerCode: string,
): Promise<{ code: PaymentMethodCode; enabled: boolean }[]> {
  const { data } = await axiosInstance.get<{ code: PaymentMethodCode; enabled: boolean }[]>(
    `${base(sellerCode)}`,
  );
  return data;
}

// Fiserv: tarjetas y cuotas (#635)
export async function listFiservCards(sellerCode: string): Promise<FiservCardType[]> {
  const { data } = await axiosInstance.get<FiservCardType[]>(`${base(sellerCode)}/fiserv/cards`);
  return data;
}

export async function listFiservInstallments(sellerCode: string): Promise<FiservInstallment[]> {
  const { data } = await axiosInstance.get<FiservInstallment[]>(
    `${base(sellerCode)}/fiserv/installments`,
  );
  return data;
}

export async function createFiservInstallment(
  sellerCode: string,
  payload: FiservInstallmentInput,
): Promise<FiservInstallment> {
  const { data } = await axiosInstance.post<FiservInstallment>(
    `${base(sellerCode)}/fiserv/installments`,
    payload,
  );
  return data;
}

export async function updateFiservInstallment(
  sellerCode: string,
  id: number,
  payload: FiservInstallmentInput,
): Promise<FiservInstallment> {
  const { data } = await axiosInstance.put<FiservInstallment>(
    `${base(sellerCode)}/fiserv/installments/${id}`,
    payload,
  );
  return data;
}

export async function deleteFiservInstallment(sellerCode: string, id: number): Promise<void> {
  await axiosInstance.delete(`${base(sellerCode)}/fiserv/installments/${id}`);
}

// Offline: ABM (#635)
export async function listOfflineMethods(sellerCode: string): Promise<OfflinePaymentMethod[]> {
  const { data } = await axiosInstance.get<OfflinePaymentMethod[]>(`${base(sellerCode)}/offline`);
  return data;
}

/** Sube el ícono a la media de Magento; devuelve el path a guardar y la URL del preview. */
export async function uploadOfflineIcon(
  sellerCode: string,
  file: Blob,
): Promise<{ path: string; url: string | null }> {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axiosInstance.post<{ path: string; url: string | null }>(
    `${base(sellerCode)}/offline-icon`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function createOfflineMethod(
  sellerCode: string,
  payload: OfflinePaymentMethod,
): Promise<OfflinePaymentMethod> {
  const { data } = await axiosInstance.post<OfflinePaymentMethod>(
    `${base(sellerCode)}/offline`,
    payload,
  );
  return data;
}

export async function updateOfflineMethod(
  sellerCode: string,
  code: string,
  payload: Omit<OfflinePaymentMethod, "code">,
): Promise<OfflinePaymentMethod> {
  const { data } = await axiosInstance.put<OfflinePaymentMethod>(
    `${base(sellerCode)}/offline/${encodeURIComponent(code)}`,
    payload,
  );
  return data;
}

export async function deleteOfflineMethod(sellerCode: string, code: string): Promise<void> {
  await axiosInstance.delete(`${base(sellerCode)}/offline/${encodeURIComponent(code)}`);
}
